import { Fragment, useEffect, useState } from "react";
import {
  keepPreviousData,
  useInfiniteQuery,
  useQuery,
} from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Loader2, Search, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { trpc } from "@/trpc";
import {
  USAGE_BILLING_COLUMN_COUNT,
  USAGE_BILLING_COLUMNS,
} from "./_components/usage-billing-columns";
import { groupUnitsByOrganization } from "./_components/usage-billing-group-utils";
import { UsageBillingMonthPicker } from "./_components/usage-billing-month-picker";
import { getCurrentUsageBillingMonthKey } from "./_components/usage-billing-time";
import { UsageBillingOrgGroupHeader } from "./_components/usage-billing-org-group-header";
import { UsageBillingUnitRow } from "./_components/usage-billing-unit-row";
import { type UnitScope } from "./_components/usage-billing-unit-scope";
import { UsageBillingUnitScopeControl } from "./_components/usage-billing-unit-scope-control";
import { useInfiniteScrollSentinel } from "./_components/use-infinite-scroll-sentinel";

const SUMMARY_PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 300;

function getEmptyMessage(scope: UnitScope): string {
  if (scope === "overage") {
    return "No business units with overage for this period";
  }
  if (scope === "all") {
    return "No business units found";
  }
  return "No usage recorded for this period";
}

function UsageBillingPage() {
  const [monthKey, setMonthKey] = useState(getCurrentUsageBillingMonthKey);
  const [unitScope, setUnitScope] = useState<UnitScope>("all");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [expandedUnitIds, setExpandedUnitIds] = useState<Set<string>>(
    new Set(),
  );
  const userQuery = useQuery(trpc.user.getUser.queryOptions());
  const canManageInvoices = userQuery.data?.is_admin === true;

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const summariesQuery = useInfiniteQuery(
    trpc.adminUsageBilling.getUnitSummaries.infiniteQueryOptions(
      {
        month: monthKey,
        search: search || undefined,
        scope: unitScope,
        limit: SUMMARY_PAGE_SIZE,
      },
      {
        getNextPageParam: (page) => page.nextCursor ?? undefined,
        // Keeps the current rows on screen (dimmed) while a month, search, or
        // scope change fetches, instead of collapsing back to skeletons.
        placeholderData: keepPreviousData,
      },
    ),
  );

  const loadMoreRef = useInfiniteScrollSentinel({
    hasNextPage: summariesQuery.hasNextPage,
    isFetchingNextPage: summariesQuery.isFetchingNextPage,
    fetchNextPage: summariesQuery.fetchNextPage,
  });

  const units = summariesQuery.data?.pages.flatMap((page) => page.items) ?? [];
  const orgGroups = groupUnitsByOrganization(units);

  // Under the overage scope the server bounds each scan and may return an
  // empty page with more candidates left. Auto-fetching those would drain the
  // whole dataset unattended, so the sentinel pauses after an empty page and
  // the admin continues explicitly.
  const lastPage = summariesQuery.data?.pages.at(-1);
  const overageScanPaused =
    unitScope === "overage" &&
    summariesQuery.hasNextPage &&
    lastPage !== undefined &&
    lastPage.items.length === 0;

  const toggleUnit = (unitId: string) => {
    setExpandedUnitIds((previous) => {
      const next = new Set(previous);
      if (next.has(unitId)) {
        next.delete(unitId);
      } else {
        next.add(unitId);
      }
      return next;
    });
  };

  // While overage candidates remain unscanned, an empty table means "nothing
  // found yet", not "no results".
  const emptyMessage =
    unitScope === "overage" &&
    (summariesQuery.hasNextPage || summariesQuery.isFetchingNextPage)
      ? "No overage found in the units scanned so far"
      : getEmptyMessage(unitScope);

  return (
    <div className="w-full p-2 pt-4 md:pt-8 md:p-8 min-h-screen bg-gray-100">
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-title-md2 font-bold text-black dark:text-white">
            Usage Billing
          </h2>
          <UsageBillingMonthPicker monthKey={monthKey} onChange={setMonthKey} />
        </div>
        <p className="mb-6 text-sm text-muted-foreground">
          Totals cover billing cycles starting in the selected month, including
          each cycle’s recorded usage in the following month. Every started
          cycle has its full base fee and allowance; no proration applies.
          Expand a unit to see its cycles and a separate calendar-month event
          ledger.
        </p>

        <div
          className={cn(
            "rounded-md border bg-white transition-opacity",
            summariesQuery.isPlaceholderData && "opacity-60",
          )}
        >
          <div className="p-4 flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder="Search by organization or unit..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="pl-10 pr-10"
              />
              {searchInput && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSearchInput("")}
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8 p-0 hover:bg-gray-100"
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
            <div className="sm:ml-auto">
              <UsageBillingUnitScopeControl
                scope={unitScope}
                onChange={setUnitScope}
              />
            </div>
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Business unit</TableHead>
                {USAGE_BILLING_COLUMNS.map((column) => (
                  <TableHead key={column.categoryCode} className="text-right">
                    {column.columnLabel}
                  </TableHead>
                ))}
                <TableHead className="text-right">Provider wages</TableHead>
                <TableHead className="text-right">Cycle totals</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {summariesQuery.isLoading ? (
                Array.from({ length: 5 }).map((_, index) => (
                  <TableRow key={`loading-${index}`}>
                    {Array.from({ length: USAGE_BILLING_COLUMN_COUNT }).map(
                      (__, cell) => (
                        <TableCell key={cell}>
                          <Skeleton className="h-4 w-16 rounded-full" />
                        </TableCell>
                      ),
                    )}
                  </TableRow>
                ))
              ) : units.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={USAGE_BILLING_COLUMN_COUNT}
                    className="py-16 text-center text-gray-500"
                  >
                    {emptyMessage}
                  </TableCell>
                </TableRow>
              ) : (
                orgGroups.map((group) => (
                  <Fragment key={group.organizationId}>
                    <UsageBillingOrgGroupHeader
                      group={group}
                      columnCount={USAGE_BILLING_COLUMN_COUNT}
                    />
                    {group.units.map((unit) => (
                      <UsageBillingUnitRow
                        key={unit.organizationBusinessUnitId}
                        unit={unit}
                        monthKey={monthKey}
                        canManageInvoices={canManageInvoices}
                        isExpanded={expandedUnitIds.has(
                          unit.organizationBusinessUnitId,
                        )}
                        onToggle={() =>
                          toggleUnit(unit.organizationBusinessUnitId)
                        }
                      />
                    ))}
                  </Fragment>
                ))
              )}
            </TableBody>
          </Table>

          {summariesQuery.hasNextPage && !overageScanPaused && (
            <div
              ref={loadMoreRef}
              className="flex justify-center py-4 border-t"
            >
              {summariesQuery.isFetchingNextPage && (
                <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
              )}
            </div>
          )}

          {overageScanPaused && (
            <div className="flex items-center justify-center gap-3 py-4 border-t text-sm text-gray-500">
              <span>More units remain unscanned.</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => summariesQuery.fetchNextPage()}
                disabled={summariesQuery.isFetchingNextPage}
              >
                {summariesQuery.isFetchingNextPage
                  ? "Scanning…"
                  : "Scan more units"}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export const Route = createFileRoute("/_admin/admin/usage-billing/")({
  component: UsageBillingPage,
});
